import type { Countries, Partner, SingleBill } from '@ctct/domain-layer';

import { ValidationError } from '../../../errors';
import type { UIService } from '../../../managers/_shared/Form/services/UIService';
import type { SingleBillInfoFormPageStore, SingleBillInfoFormValues } from '../types';

export class DataPreparationService {
  constructor(
    private pageStore: SingleBillInfoFormPageStore,
    private isEditing: boolean,
    private countries: Countries,
    private singleBill: SingleBill,
    private partner: Partner,
    private getDefaultValues: () => SingleBillInfoFormValues,
    private uiService: UIService<SingleBillInfoFormValues>
  ) {}

  public api() {
    return {
      getValuesForApiRequest: async () => {
        // If `isDisplayingCard` is `true`, assume the form is not mounted.
        // In that case, retrieve the default values from the backend.
        if (this.pageStore.getState().isDisplayingCard) {
          const defaultValues = this.getDefaultValues();

          return this.createApiPayload(defaultValues);
        }

        if (!this.uiService.getFormValuesFn) throw new Error('get form values function is not added');

        const isFormValid = await this.uiService.triggerValidation?.();

        if (!isFormValid) {
          throw new ValidationError();
        }

        // If the form is mounted, retrieve the current form values.
        const formValues = this.uiService.getFormValuesFn();

        return this.createApiPayload(formValues);
      },
    };
  }

  public data() {
    return {};
  }

  public handlers() {
    return {};
  }

  private createApiPayload(formValues: SingleBillInfoFormValues) {
    return {
      customer: {
        address1: formValues.address1,
        address2: formValues.address2 ?? '',
        city: formValues.city,
        companyname: formValues.companyname,
        email: formValues.email,
        country: formValues.country,
        firstname: formValues.firstname,
        lastname: formValues.lastname,
        phone: formValues.phone,
        state: formValues.state ?? '',
        zipcode: formValues.zipcode,
        billingDayOfMonth: Number(formValues.billingDayOfMonth),
        currencyCd: formValues.currency,
        vatNumber: this.countries.isShowingSingleBillTaxNumber(formValues.country) ? formValues.vatNumber : '',
        addressValidationRequired: this.isEditing ? this.singleBill.getAddressValidationRequired() : true,
        billingType: this.isEditing ? this.singleBill.getBillingType() : { name: 'S' },
        customerId: this.isEditing ? this.singleBill.getCustomerId() : this.partner.getPartnerId(),
      },
      partner_contact: {
        partner_contact_email: formValues.partnerContactEmail,
        partner_contact_name: formValues.partnerContactName,
      },
    };
  }
}
